import _ from 'lodash';

import { CondensedVisitType, VisitType } from 'ev-types';

import { sanitizeId, sanitizeNullableId } from 'ev-api/utils';

import {
  IncludedCustomFormQuestion,
  IncludedIntakeForm,
  IncludedIntakeFormQuestion,
  VisitTypeResponse,
  VisitTypeResponseForGetVisits,
  VisitTypesResponse,
} from './responses';

export const visitTypeTransformer = (
  response: VisitTypeResponse,
): VisitType => {
  const { data } = response;
  const { attributes } = data;

  const intakeFormId = data.relationships.active_intake_form.data.id;

  const intakeForm = _.chain(response.included)
    .filter(isIntakeForm)
    .find({
      id: intakeFormId,
    })
    .value();

  const reasonQuestionIds = _.map(
    intakeForm.relationships.reasons.data,
    reason => reason.id,
  );

  const descriptionQuestionIds = _.map(
    intakeForm.relationships.descriptions.data,
    reason => reason.id,
  );

  const questionIds = reasonQuestionIds.concat(descriptionQuestionIds);

  return {
    ...data,
    id: sanitizeId(data.id),
    attributes: {
      ...attributes,
      id: sanitizeId(attributes.id),
      practice_id: sanitizeId(attributes.practice_id),
      first_available_provider: attributes.first_available_provider
        ? sanitizeId(attributes.first_available_provider)
        : null,
      billing_default_price_cents: _.isString(
        attributes.billing_default_price_cents,
      )
        ? parseFloat(attributes.billing_default_price_cents)
        : attributes.billing_default_price_cents,
      insurance_manual_price_cents: _.isString(
        attributes.insurance_manual_price_cents,
      )
        ? parseFloat(attributes.insurance_manual_price_cents)
        : attributes.insurance_manual_price_cents,
      provider_groups: _.map(
        attributes.provider_groups,
        ({ id, available }) => ({
          id: sanitizeId(id),
          available: available,
        }),
      ),
      provider_assignment: {
        ...attributes.provider_assignment,
        individual_provider_id: sanitizeNullableId(
          attributes.provider_assignment.individual_provider_id,
        ),
        provider_group_id: sanitizeNullableId(
          attributes.provider_assignment.provider_group_id,
        ),
      },
      disable_unavailable_providers: attributes.disable_unavailable_providers,
    },
    intakeForm: _.chain(response.included)
      .filter(isFormQuestion)
      .filter(question => _.includes(questionIds, question.id))
      .sortBy('id')
      .map(field => ({
        ...field.attributes,
        id: sanitizeId(field.id),
      }))
      .value(),
    customForms: _.chain(response.included)
      .filter(isCustomFormQuestion)
      .filter(
        form =>
          form.attributes.subscriber_type === 'VisitType' &&
          _.toString(response.data.id) ===
            _.toString(form.attributes.subscriber_id),
      )
      .sortBy('id')
      .map(field => ({
        ...field.attributes,

        id: sanitizeId(field.id),
        custom_form_id: sanitizeId(field.attributes.custom_form_id),
        custom_form_version_id: sanitizeId(
          field.attributes.custom_form_version_id,
        ),
        subscriber_id: sanitizeId(field.attributes.subscriber_id),
      }))
      .value(),
  };
};

export const visitTypesTransformer = (
  response: VisitTypesResponse,
): VisitType[] => {
  return _.map(response.data, responseData =>
    visitTypeTransformer({
      data: responseData,
      included: response.included,
    }),
  );
};

export const visitTypeForGetVisitsTransformer = (
  response: VisitTypeResponseForGetVisits,
): CondensedVisitType => {
  const { attributes } = response;

  return {
    ...response,
    id: sanitizeId(response.id),
    attributes: {
      ...attributes,
      id: sanitizeId(response.id),
    },
  };
};

const isFormQuestion = (
  value:
    | IncludedIntakeForm
    | IncludedIntakeFormQuestion
    | IncludedCustomFormQuestion,
): value is IncludedIntakeFormQuestion => {
  return value.type === 'dynamic_form_field';
};

const isCustomFormQuestion = (
  value:
    | IncludedIntakeForm
    | IncludedIntakeFormQuestion
    | IncludedCustomFormQuestion,
): value is IncludedCustomFormQuestion => {
  return value.type === 'custom_form_subscription';
};

const isIntakeForm = (
  value:
    | IncludedIntakeForm
    | IncludedIntakeFormQuestion
    | IncludedCustomFormQuestion,
): value is IncludedIntakeForm => {
  return value.type === 'intake_form';
};
